import React from 'react';
import TouchButton from '../ui/TouchButton';
import { TouchFormGroup } from '../ui/TouchForm';

const SafetyZoneFilter = ({ 
  selectedZones, 
  onZonesChange,
  minRating,
  maxRating,
  onRatingRangeChange 
}) => {
  const safetyZones = [
    {
      id: 'safe',
      label: 'Safe Zones',
      description: '4.0+ Safety Rating',
      color: '#4CAF50',
      icon: '🟢',
      ratingRange: [4.0, 5.0]
    },
    {
      id: 'moderate',
      label: 'Moderate Zones',
      description: '2.5-4.0 Safety Rating',
      color: '#FF9800',
      icon: '🟡',
      ratingRange: [2.5, 4.0]
    },
    {
      id: 'caution',
      label: 'Caution Zones',
      description: 'Below 2.5 Safety Rating',
      color: '#F44336',
      icon: '🔴',
      ratingRange: [0, 2.5]
    }
  ];

  const toggleZone = (zoneId) => {
    const newZones = selectedZones.includes(zoneId)
      ? selectedZones.filter(id => id !== zoneId)
      : [...selectedZones, zoneId];
    onZonesChange(newZones);
  };

  const handleRatingChange = (type, value) => {
    if (type === 'min') {
      onRatingRangeChange(value, maxRating);
    } else {
      onRatingRangeChange(minRating, value);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Safety Zone Selection */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          Select Safety Zones:
        </label>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {safetyZones.map(zone => (
            <SafetyZoneOption
              key={zone.id}
              zone={zone}
              isSelected={selectedZones.includes(zone.id)}
              onToggle={() => toggleZone(zone.id)}
            />
          ))}
        </div>
      </TouchFormGroup>

      {/* Custom Rating Range */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          Custom Safety Rating Range:
        </label>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
              Minimum Rating
            </label>
            <select
              value={minRating || ''}
              onChange={(e) => handleRatingChange('min', e.target.value ? parseFloat(e.target.value) : null)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
            >
              <option value="">Any</option>
              {[1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5].map(rating => (
                <option key={rating} value={rating}>
                  {rating.toFixed(1)} ⭐
                </option>
              ))}
            </select>
          </div>
          
          <div style={{ 
            fontSize: '14px', 
            color: '#666', 
            padding: '0 8px',
            alignSelf: 'flex-end',
            paddingBottom: '8px'
          }}>
            to
          </div>
          
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
              Maximum Rating
            </label>
            <select
              value={maxRating || ''}
              onChange={(e) => handleRatingChange('max', e.target.value ? parseFloat(e.target.value) : null)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
            >
              <option value="">Any</option>
              {[2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0].map(rating => (
                <option key={rating} value={rating}>
                  {rating.toFixed(1)} ⭐
                </option>
              ))}
            </select>
          </div>
        </div>
      </TouchFormGroup>

      {/* Quick Rating Buttons */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          Quick Rating Filters:
        </label>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {[
            { min: 4.5, max: null, label: '4.5+ ⭐ Excellent' },
            { min: 4.0, max: null, label: '4.0+ ⭐ Very Good' },
            { min: 3.5, max: null, label: '3.5+ ⭐ Good' },
            { min: 3.0, max: null, label: '3.0+ ⭐ Average' }
          ].map((preset, index) => (
            <TouchButton
              key={index}
              onClick={() => onRatingRangeChange(preset.min, preset.max)}
              variant={minRating === preset.min && maxRating === preset.max ? 'primary' : 'outline'}
              size="small"
              style={{ fontSize: '12px' }}
            >
              {preset.label}
            </TouchButton>
          ))}
        </div>
      </TouchFormGroup>

      {/* Active Filter Summary */}
      {(selectedZones.length > 0 || minRating || maxRating) && (
        <div style={{
          padding: '12px',
          backgroundColor: '#e3f2fd',
          borderRadius: '8px',
          border: '1px solid #2196F3'
        }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#1976D2', marginBottom: '6px' }}>
            Active Safety Filters:
          </div>
          <div style={{ fontSize: '12px', color: '#333' }}>
            {selectedZones.length > 0 && (
              <div>Zones: {selectedZones.join(', ')}</div>
            )}
            {(minRating || maxRating) && (
              <div>
                Rating: {minRating ? `${minRating.toFixed(1)}` : 'Any'} - {maxRating ? `${maxRating.toFixed(1)}` : 'Any'} ⭐
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SafetyZoneOption = ({ zone, isSelected, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      display: 'flex',
      alignItems: 'center',
      padding: '12px',
      borderRadius: '8px',
      border: `2px solid ${isSelected ? zone.color : '#e0e0e0'}`,
      backgroundColor: isSelected ? `${zone.color}15` : '#ffffff',
      cursor: 'pointer',
      transition: 'all 150ms ease'
    }}
  >
    <div style={{
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: zone.color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
      marginRight: '12px'
    }}>
      {zone.icon}
    </div>
    
    <div style={{ flex: 1 }}>
      <div style={{
        fontSize: '14px',
        fontWeight: '600',
        color: '#333',
        marginBottom: '2px'
      }}>
        {zone.label}
      </div>
      <div style={{
        fontSize: '12px',
        color: '#666'
      }}>
        {zone.description}
      </div>
    </div>
    
    <div style={{
      width: '20px',
      height: '20px',
      borderRadius: '4px',
      border: `2px solid ${zone.color}`,
      backgroundColor: isSelected ? zone.color : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      color: 'white'
    }}>
      {isSelected && '✓'}
    </div>
  </div>
);

export default SafetyZoneFilter;